/**
 * DraftingList — unified list view for /drafting (AB#1573).
 *
 * One merged list of everything a user drafts. Each item is either a
 * "Document" (kind='draft') or a "Strategy" (kind='strategy'); the two
 * collections are fetched separately from the backend and combined here so
 * the user sees a single list, filterable by kind. Mirrors the platform's
 * standard list-page structure (Header + FilterBarWithTabs + grid/table).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  PiFileTextLight,
  PiLightbulbLight,
  PiPlusLight,
  PiClockLight,
  PiSparkleLight,
  PiBriefcaseLight,
  PiArrowRightLight,
  PiFileLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiArrowClockwiseLight,
  PiClockCounterClockwiseLight,
  PiDownloadSimpleLight,
  PiTrashLight,
} from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/functions';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  fetchDraftingList,
  downloadDrafting,
  deleteDrafting,
  DraftingKind,
  DraftingItem,
} from '../../store/slices/draftingSlice';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import type { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { useDraftingListPubSub } from '../../hooks/useDraftingListPubSub';

import ActivityDrawer from '../shared/activity/ActivityDrawer';
import { humanDocType, draftTitle } from './draftLabels';

interface DraftingListProps {
  /** Open the creation wizard (kind is chosen in the wizard's first step). */
  onNew: () => void;
}

// Everything is a "draft"; the two creatable kinds are Document + Strategy.
const KIND_LABEL: Record<DraftingKind, string> = { draft: 'Document', strategy: 'Strategy' };
const KIND_COLOR: Record<DraftingKind, BadgeColor> = { draft: 'blue', strategy: 'amber' };
// Kind is a filter option (Documents / Strategies) — not a separate tab.
const KIND_FILTER_OPTIONS = [
  { value: 'draft',    label: 'Documents' },
  { value: 'strategy', label: 'Strategies' },
];

const STATUS_CONFIG: Record<
  DraftingItem['status'],
  { badge: BadgeColor; icon: React.ReactNode; label: string }
> = {
  not_generated: { badge: 'gray',   icon: <PiFileLight size={11} />,           label: 'Not generated' },
  queued:        { badge: 'blue',   icon: <PiArrowClockwiseLight size={11} className="animate-fadePulse" />, label: 'Queued' },
  processing:    { badge: 'yellow', icon: <PiSparkleLight size={11} className="animate-fadePulse" />,       label: 'Processing' },
  completed:     { badge: 'green',  icon: <PiCheckCircleLight size={11} />,    label: 'Completed' },
  failed:        { badge: 'red',    icon: <PiWarningCircleLight size={11} />,  label: 'Failed' },
};

const STATUS_ORDER: DraftingItem['status'][] = ['completed', 'processing', 'queued', 'not_generated', 'failed'];

/*
  The document type as a person reads it — '' when the id has nothing readable
  in it. A draft built on one of your own structure templates carries the
  synthetic id `template_<uuid>`, and de-slugging that printed a raw uuid into
  the Name column, the Type column and the card. See `./draftLabels`.
*/
const humanizeType = (id: string): string => humanDocType(id);

// Browser-locale + UTC-aware date/time — shared platform formatter.
const fmtDate = (iso: string | null): string => formatDateTime(iso ?? undefined);

function DraftingList({ onNew }: Readonly<DraftingListProps>) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const drafts = useAppSelector((s: RootState) => s.drafting.drafts);
  const strategies = useAppSelector((s: RootState) => s.drafting.strategies);
  const generatingIds = useAppSelector((s: RootState) => s.drafting.generatingIds);
  const loading = useAppSelector((s: RootState) => s.drafting.listLoading);
  const error = useAppSelector((s: RootState) => s.drafting.error);

  const allItems = useMemo(() => [...drafts, ...strategies], [drafts, strategies]);

  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [sortValue, setSortValue] = useState('newest');
  const [tblSortBy, setTblSortBy] = useState('created');
  const [tblSortOrder, setTblSortOrder] = useState<'asc' | 'desc'>('desc');
  const [auditTarget, setAuditTarget] = useState<{ id: string; name: string; kind: DraftingKind } | null>(null);
  // True until the first fetch of BOTH collections settles. Prevents the empty
  // state ("Nothing drafted yet") from flashing before results arrive — we show
  // the skeleton until we actually know the list is empty.
  const [initialLoad, setInitialLoad] = useState(true);

  // Fetch BOTH collections — the list is merged.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      dispatch(fetchDraftingList({ kind: 'draft' })),
      dispatch(fetchDraftingList({ kind: 'strategy' })),
    ]).finally(() => { if (!cancelled) setInitialLoad(false); });
    return () => { cancelled = true; };
  }, [dispatch]);

  // Live status/progress for any in-flight items across both kinds.
  useDraftingListPubSub(allItems);

  // ── Filter option sets (kind is now a filter, not a tab) ────────────────
  const kindScoped = useMemo(() => {
    const kindF = selectedFilters.kind ?? [];
    return kindF.length ? allItems.filter((i) => kindF.includes(i.kind)) : allItems;
  }, [allItems, selectedFilters]);

  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    kindScoped.forEach((i) => {
      // An id with nothing readable in it (`template_<uuid>`) would otherwise
      // become a blank, unselectable row in the Type filter.
      const label = humanizeType(i.document_type_id);
      if (i.document_type_id && label) seen.set(i.document_type_id, label);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
  }, [kindScoped]);

  const statusOptions = useMemo(() => {
    const present = new Set(kindScoped.map((i) => i.status));
    return STATUS_ORDER.filter((s) => present.has(s)).map((s) => ({ value: s, label: STATUS_CONFIG[s].label }));
  }, [kindScoped]);

  const filterSections = useMemo(() => {
    const sections: { id: string; name: string; options: { value: string; label: string }[] }[] = [
      { id: 'kind', name: 'Kind', options: KIND_FILTER_OPTIONS },
    ];
    if (typeOptions.length > 0) sections.push({ id: 'type', name: 'Type', options: typeOptions });
    if (statusOptions.length > 0) sections.push({ id: 'status', name: 'Status', options: statusOptions });
    return sections;
  }, [typeOptions, statusOptions]);

  const sortOptions = useMemo(() => ([
    { value: 'newest',    label: 'Newest first', current: sortValue === 'newest' },
    { value: 'oldest',    label: 'Oldest first', current: sortValue === 'oldest' },
    { value: 'name_asc',  label: 'Name (A–Z)',   current: sortValue === 'name_asc' },
    { value: 'name_desc', label: 'Name (Z–A)',   current: sortValue === 'name_desc' },
  ]), [sortValue]);

  // ── Filter (kind + search + type + status) ──────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const typeF = selectedFilters.type ?? [];
    const statusF = selectedFilters.status ?? [];
    return kindScoped.filter((i) => {
      if (typeF.length && !typeF.includes(i.document_type_id)) return false;
      if (statusF.length && !statusF.includes(i.status)) return false;
      if (q) {
        const hay = [i.name, i.description, humanizeType(i.document_type_id), i.matter_title]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [kindScoped, search, selectedFilters]);

  const sortList = (list: DraftingItem[], by: string, order: 'asc' | 'desc') => {
    const name = (i: DraftingItem) => draftTitle(i, '').toLowerCase();
    const ms = (i: DraftingItem) => (i.created_at ? new Date(i.created_at).getTime() : 0);
    const cmp = (a: DraftingItem, b: DraftingItem) => {
      switch (by) {
        case 'name':   return name(a).localeCompare(name(b));
        case 'kind':   return a.kind.localeCompare(b.kind);
        case 'type':   return humanizeType(a.document_type_id).localeCompare(humanizeType(b.document_type_id));
        case 'status': return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
        default:       return ms(a) - ms(b);
      }
    };
    const sorted = [...list].sort(cmp);
    return order === 'desc' ? sorted.reverse() : sorted;
  };

  const gridItems = useMemo(() => {
    const map: Record<string, [string, 'asc' | 'desc']> = {
      newest: ['created', 'desc'], oldest: ['created', 'asc'],
      name_asc: ['name', 'asc'], name_desc: ['name', 'desc'],
    };
    const [by, order] = map[sortValue] ?? ['created', 'desc'];
    return sortList(filtered, by, order);
  }, [filtered, sortValue]);

  const tableItems = useMemo(
    () => sortList(filtered, tblSortBy, tblSortOrder),
    [filtered, tblSortBy, tblSortOrder],
  );

  // ── Row actions ─────────────────────────────────────────────────────────
  const handleDownload = (item: DraftingItem) => {
    const filename = draftTitle(item, t('Untitled draft'));
    dispatch(downloadDrafting({ kind: item.kind, uuid: item.uuid, filename })).then((res: any) => {
      if (downloadDrafting.rejected.match(res)) {
        dispatch(showFeedbackModal({ modalType: 'error', message: res.payload || t('Failed to download document'), showModal: true, duration: 5000 }));
      }
    });
  };

  const handleDelete = (item: DraftingItem) => {
    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      message: t('Delete {{kind}}', { kind: t(KIND_LABEL[item.kind]).toLowerCase() }),
      subMsg: t('This will be permanently removed. This action cannot be undone.'),
      isDest: true,
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      onConfirm: async () => {
        const res = await dispatch(deleteDrafting({ kind: item.kind, uuid: item.uuid }));
        dispatch(hideModal());
        if (deleteDrafting.fulfilled.match(res)) {
          dispatch(showFeedbackModal({ modalType: 'success', message: t('Deleted'), showModal: true, duration: 3000 }));
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  const rowActions = useMemo(() => ([
    {
      label: t('Activity log'),
      onClick: (row: any) => setAuditTarget({ id: row.uuid, name: row._name || '', kind: row._kind }),
      icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
    },
    {
      label: t('Download (Word)'),
      onClick: (row: any) => { const it = allItems.find((i) => i.uuid === row.uuid); if (it) handleDownload(it); },
      icon: <PiDownloadSimpleLight className="w-4 h-4" />,
      hidden: (row: any) => row._status !== 'completed',
    },
    {
      label: t('Delete'),
      onClick: (row: any) => { const it = allItems.find((i) => i.uuid === row.uuid); if (it) handleDelete(it); },
      icon: <PiTrashLight className="w-4 h-4" />,
      isDestructive: true,
    },
  ]), [allItems, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Table data ──────────────────────────────────────────────────────────
  const columns = ['Name', 'Kind', 'Type', 'Status', 'Matter', 'Words', 'Created', 'Actions'];
  const tableData = useMemo(() => tableItems.map((item) => {
    const sc = generatingIds.includes(item.uuid) ? STATUS_CONFIG.processing : STATUS_CONFIG[item.status];
    return {
      id: item.uuid,
      uuid: item.uuid,
      _name: draftTitle(item, t('Untitled draft')),
      _status: item.status,
      _kind: item.kind,
      Name: <span className="text-fg-strong truncate" title={draftTitle(item, t('Untitled draft'))}>{draftTitle(item, t('Untitled draft'))}</span>,
      Kind: <Badge color={KIND_COLOR[item.kind]}>{t(KIND_LABEL[item.kind])}</Badge>,
      Type: <span className="text-gray-600 dark:text-gray-300">{humanizeType(item.document_type_id) || '—'}</span>,
      Status: <Badge color={sc.badge}><span className="flex items-center gap-1">{sc.icon}{t(sc.label)}</span></Badge>,
      Matter: item.matter_uuid && item.matter_title ? (
        <Link to={`/matters/${item.matter_uuid}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-primary-600 hover:underline dark:text-primary-400">
          <PiBriefcaseLight size={12} />
          <span className="truncate max-w-[12rem]">{item.matter_title}</span>
        </Link>
      ) : <span className="text-xs text-gray-400">{t('Standalone')}</span>,
      Words: item.word_count ? item.word_count.toLocaleString() : '—',
      Created: fmtDate(item.created_at),
    };
  }), [tableItems, generatingIds, t]);

  const bodyEmpty = !loading && !initialLoad && filtered.length === 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header
        breadcrumbs={[{ name: t('Drafting'), href: '/drafting', current: true }]}
        actions={[
          { label: t('Add Draft'), onClick: onNew, icon: <PiPlusLight className="w-4 h-4" />, variant: 'primary' },
        ]}
      />

      <FilterBarWithTabs
        itemCount={filtered.length}
        totalItemCount={allItems.length}
        itemCountLabel="items"
        // No tabs — Kind is just another filter (Documents / Strategies).
        filters={filterSections}
        selectedFilters={selectedFilters}
        onFilterChange={(id, vals) => setSelectedFilters((p) => ({ ...p, [id]: vals }))}
        onClearAllFilters={() => setSelectedFilters({})}
        sortOptions={sortOptions}
        sortValue={sortValue}
        onSortChange={setSortValue}
        onSearchChange={setSearch}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      <div className="flex-1 overflow-hidden">
        {error && (
          <div className="mt-3 flex items-start gap-3 border-y border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-800 dark:border-danger-800 dark:bg-danger-900/30 dark:text-danger-200">
            <PiWarningCircleLight size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/*
          Until BOTH collections have landed, this is a skeleton — not a
          partial list.

          The gate used to be `&& allItems.length === 0`, which let the first
          collection to arrive render as if it were the whole list. Drafts and
          strategies are separate requests, and on this backend they are far
          apart: strategies answer in ~6s, documents in ~19s. So for thirteen
          seconds the page showed one strategy under "Showing 1 of 1 items" —
          not "still loading", but a confident claim that one item is all there
          is. That is what "the documents are missing" looked like.

          `initialLoad` is already exactly the right condition: false only once
          the first fetch of both has settled.
        */}
        {initialLoad ? (
          viewMode === 'table' ? (
            <Skeleton variant="table" rows={8} columns={columns.length} />
          ) : (
            <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="glass h-40 animate-fadePulse rounded-xl" />
              ))}
            </div>
          )
        ) : bodyEmpty ? (
          <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
            <PiFileTextLight size={40} className="mb-3 text-gray-300 dark:text-gray-600" />
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              {allItems.length === 0 ? t('Nothing drafted yet') : t('No matches')}
            </p>
            <p className="mb-5 text-sm text-gray-400 dark:text-gray-500">
              {allItems.length === 0
                ? t('Create a document or a strategy to get started.')
                : t('Try adjusting your search or filters.')}
            </p>
            {allItems.length === 0 && (
              <Button variant="primary" size="small" onClick={onNew} icon={<PiPlusLight size={14} />}>
                {t('New')}
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="h-full overflow-auto py-4">
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {gridItems.map((item) => (
                <DraftingCard key={item.uuid} item={item} isGenerating={generatingIds.includes(item.uuid)} />
              ))}
            </ul>
          </div>
        ) : (
          <div className="h-full overflow-hidden">
            <Table
              columns={columns}
              data={tableData}
              onRowClick={(row: any) => history.push(`/drafting/${row.uuid}`)}
              actions={rowActions}
              sortableColumns={['Name', 'Kind', 'Type', 'Status', 'Created']}
              sortBy={tblSortBy}
              sortOrder={tblSortOrder}
              onSort={(by, order) => { setTblSortBy(by); setTblSortOrder(order); }}
              total_items={allItems.length}
              total_showing_items={filtered.length}
              setIsShowing
              showingLabel={t('items')}
              resizableColumns
              storageKeyPrefix="drafting"
              defaultColumnWidths={{ Name: 260, Kind: 120, Type: 190, Status: 130, Matter: 190, Words: 90, Created: 150 }}
            />
          </div>
        )}
      </div>

      {/* Activity-log slide-over (shared AuditLogPanel) */}
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel entityType={auditTarget.kind} entityId={auditTarget.id} entityName={auditTarget.name} onClose={() => setAuditTarget(null)} />
        </ActivityDrawer>
      )}
    </div>
  );
}

// ─── Grid card ──────────────────────────────────────────────────────────────

function DraftingCard({ item, isGenerating }: Readonly<{ item: DraftingItem; isGenerating: boolean }>) {
  const { t } = useTranslation();
  const sc = isGenerating ? STATUS_CONFIG.processing : STATUS_CONFIG[item.status];
  const isStrategy = item.kind === 'strategy';
  const accent = isStrategy
    ? 'text-warning-500 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20'
    : 'text-primary-500 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20';
  const KindIcon = isStrategy ? PiLightbulbLight : PiFileTextLight;
  const attachmentCount = (item.attachments ?? []).length;

  return (
    <li>
      <Link
        to={`/drafting/${item.uuid}`}
        className="glass group flex h-full flex-col overflow-hidden rounded-xl shadow-sm transition-all hover:-translate-y-0.5 hover: hover:shadow-md dark:hover:"
      >
        <div className="flex flex-1 flex-col p-5">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${accent}`}>
                <KindIcon size={17} />
              </span>
              <div className="min-w-0">
                <h3 className="line-clamp-2 text-sm font-semibold text-gray-900 dark:text-white">
                  {draftTitle(item, t('Untitled draft'))}
                </h3>
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t(KIND_LABEL[item.kind])}
                </span>
              </div>
            </div>
            <Badge color={sc.badge}>
              <span className="flex items-center gap-1">{sc.icon}{t(sc.label)}</span>
            </Badge>
          </div>

          {item.description && (
            <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{item.description}</p>
          )}

          {humanizeType(item.document_type_id) && (
            <span className="mb-3 inline-flex w-fit items-center rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700/60 dark:text-gray-300">
              {humanizeType(item.document_type_id)}
            </span>
          )}

          <div className="mt-auto flex items-center gap-3 border-t border-gray-100 pt-3 text-xs text-gray-400 dark:border-gray-700/60 dark:text-gray-500">
            <span className="inline-flex items-center gap-1">
              <PiClockLight size={11} />
              {fmtDate(item.created_at)}
            </span>
            {item.word_count != null && item.word_count > 0 && (
              <span>{item.word_count.toLocaleString()} {t('words')}</span>
            )}
            {item.matter_uuid && item.matter_title ? (
              <Link to={`/matters/${item.matter_uuid}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 truncate text-primary-600 hover:underline dark:text-primary-400">
                <PiBriefcaseLight size={11} />
                <span className="truncate max-w-[9rem]">{item.matter_title}</span>
              </Link>
            ) : attachmentCount === 0 ? (
              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-400">{t('Standalone')}</span>
            ) : null}
            <span className="ml-auto text-gray-300 transition-transform group-hover:translate-x-0.5 dark:text-gray-600">
              <PiArrowRightLight size={13} />
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}

export default DraftingList;
